import { useCallback, useEffect, useState } from "react";
import { locationKindsApi, type LocationKindBehavior, type LocationKindIcon, type LocationKindView } from "../api/locations";
import type { ApiError } from "../api/client";

/**
 * Location kinds (`GET /location-kinds`) shared by every screen that renders or
 * picks a kind. Fetched once per session and cached at module level; the admin
 * page calls `refresh()` after a write so open pickers see the change.
 */

let cache: LocationKindView[] | null = null;
let inflight: Promise<LocationKindView[]> | null = null;
const listeners = new Set<() => void>();

function sortKinds(list: LocationKindView[]): LocationKindView[] {
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "sr-Latn"));
}

function notify() {
  for (const l of listeners) l();
}

function load(force = false): Promise<LocationKindView[]> {
  if (!force && cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = locationKindsApi
    .list()
    .then((list) => {
      cache = sortKinds(list);
      inflight = null;
      notify();
      return cache;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Re-fetch the catalogue (after an admin write). Every mounted hook re-renders. */
export function refreshLocationKinds(): Promise<LocationKindView[]> {
  return load(true);
}

/** Test hook: drop the module cache so the next mount fetches again. */
export function resetLocationKindsCache(): void {
  cache = null;
  inflight = null;
}

export type LocationKindsHook = {
  /** Sorted by `sortOrder`, then name. Empty while loading or on error. */
  kinds: LocationKindView[];
  loading: boolean;
  error: string | null;
  /** Display name for a code; the code itself when unknown (deleted kind, stale cache). */
  labelOf: (code: string) => string;
  /** Behavior for a code; `NONE` when unknown. */
  behaviorOf: (code: string) => LocationKindBehavior;
  /** Admin-selected icon; a location pin when unknown. */
  iconOf: (code: string) => LocationKindIcon;
  refresh: () => Promise<LocationKindView[]>;
};

export function useLocationKinds(): LocationKindsHook {
  const [kinds, setKinds] = useState<LocationKindView[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const onChange = () => {
      if (mounted && cache) setKinds(cache);
    };
    listeners.add(onChange);
    if (cache) {
      setKinds(cache);
      setLoading(false);
    } else {
      setLoading(true);
      load()
        .then((list) => { if (mounted) { setKinds(list); setError(null); } })
        .catch((err: ApiError) => { if (mounted) setError(err.message); })
        .finally(() => { if (mounted) setLoading(false); });
    }
    return () => {
      mounted = false;
      listeners.delete(onChange);
    };
  }, []);

  const labelOf = useCallback(
    (code: string) => kinds.find((k) => k.code === code)?.name ?? code,
    [kinds],
  );
  const behaviorOf = useCallback(
    (code: string): LocationKindBehavior => kinds.find((k) => k.code === code)?.behavior ?? "NONE",
    [kinds],
  );
  const refresh = useCallback(() => refreshLocationKinds(), []);
  const iconOf = useCallback(
    (code: string): LocationKindIcon => kinds.find((k) => k.code === code)?.icon ?? "pin",
    [kinds],
  );

  return { kinds, loading, error, labelOf, behaviorOf, iconOf, refresh };
}
